import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { APP_CONSTANTS } from '../../common/constants';
import { ACCOUNT_PAYMENT_RESPONSE_CODES } from '../../common/enums/accountPayment';
import { AUTH_RESPONSE_CODES } from '../../common/enums/authResponseCodes';
import {
  CLIENT_BUILD_VERSION,
  CLIENT_BUILD_VERSION_HEADER_NAME,
  CLIENT_VERSION,
  CLIENT_VERSION_HEADER_NAME,
} from '../../common/enums/clientBuild';
import { IPC } from '../../common/ipc/channels';
import { ApiResponse } from '../../common/types';
import { useAppStore } from '../store';
import {
  dispatchOpenComputePowerPurchaseModal,
  dispatchOpenMembershipPaymentModal,
} from '../utils/paymentModalEvents';
import { dispatchAccountPaymentDataUpdated } from '../utils/accountPaymentEvents';

function resolveRequestBaseUrl() {
  const configuredBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();

  // 开发环境统一走 Vite 代理，这样 .env 里可以和生产环境一样直接写完整 API 地址，
  // 同时继续复用本地联调时的代理与 Cookie 能力，避免额外维护一套 /proxy-api 配置。
  if (import.meta.env.DEV && /^https?:\/\//i.test(configuredBaseUrl)) {
    return '/proxy-api';
  }

  return configuredBaseUrl;
}

const baseURL = resolveRequestBaseUrl();
const COMPUTE_BALANCE_REFRESH_URL_PARTS = [
  '/products/shop-categories/confirm',
  '/video-generation/generate-prompt',
  '/video-generation/adapt-prompt',
  '/insight/leads/suggestions',
  '/insight/private-leads/suggestions',
  '/insight/product-testing/search',
  '/insight/product-testing/analysis',
];
const DEMO_BLOCKED_URL_PARTS = [
  '/ecommerce/accounts/auth',
  '/sync-products',
  '/link-shop',
  '/detect-session',
  '/session-status',
  '/products/shop-categories/confirm',
  '/insight/leads/status',
  '/insight/private-leads/status',
  '/insight/hot-videos/collect',
  '/insight/potential-products/collect',
  '/selection-library',
  '/insight/competitor-accounts',
];
const DEMO_COMPUTE_POLICY_REQUESTS: Array<{ parts: string[]; scenarioCode: string }> = [
  {
    parts: [
      '/video-generation/submit',
    ],
    scenarioCode: 'CREATIVE_WRITER_VIDEO_GENERATION',
  },
  {
    parts: [
      '/insight/leads/suggestions',
      '/insight/private-leads/suggestions',
    ],
    scenarioCode: 'LEAD_SUGGESTION',
  },
  {
    parts: [
      '/insight/videos/analysis/generate',
    ],
    scenarioCode: 'VIDEO_DNA_PROMPT',
  },
  {
    parts: [
      '/insight/product-testing/search',
    ],
    scenarioCode: 'PRODUCT_TESTING_CATEGORY_MATCH',
  },
  {
    parts: [
      '/insight/product-testing/analysis',
    ],
    scenarioCode: 'PRODUCT_TESTING_DETAIL_ANALYSIS',
  },
];

// 1. 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL,
  timeout: APP_CONSTANTS.REQUEST_TIMEOUT,
  withCredentials: true, // 自动携带 sid Cookie（Cookie Session 认证）
  headers: {
    'Content-Type': 'application/json',
  },
});

let isClearingApiAuthSession = false;

interface RequestErrorMeta {
  code?: number;
  status?: number;
}

function createRequestError(message: string, meta: RequestErrorMeta = {}) {
  const nextError = new Error(message) as Error & RequestErrorMeta;
  if (typeof meta.code === 'number') {
    nextError.code = meta.code;
  }
  if (typeof meta.status === 'number') {
    nextError.status = meta.status;
  }
  return nextError;
}

function isAuthRedirectCode(code?: number) {
  return code === AUTH_RESPONSE_CODES.SESSION_EXPIRED
    || code === AUTH_RESPONSE_CODES.SESSION_INVALID
    || code === AUTH_RESPONSE_CODES.SINGLE_LOGIN_CONFLICT;
}

function handlePaymentBusinessError(code?: number) {
  if (code === ACCOUNT_PAYMENT_RESPONSE_CODES.INSUFFICIENT_COMPUTE_BALANCE) {
    dispatchOpenComputePowerPurchaseModal();
    return;
  }

  if (
    code === ACCOUNT_PAYMENT_RESPONSE_CODES.SUBSCRIPTION_REQUIRED
    || code === ACCOUNT_PAYMENT_RESPONSE_CODES.SUBSCRIPTION_EXPIRED
  ) {
    dispatchOpenMembershipPaymentModal();
  }
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function payloadHasComputeMutation(payload: unknown, depth = 0): boolean {
  if (depth > 4 || payload == null) {
    return false;
  }

  if (Array.isArray(payload)) {
    return payload.some((item) => payloadHasComputeMutation(item, depth + 1));
  }

  if (!isObjectLike(payload)) {
    return false;
  }

  const computeCharge = payload.computeCharge;
  if (isObjectLike(computeCharge)) {
    return computeCharge.charged === true || computeCharge.refunded === true;
  }

  if (
    (payload.charged === true || payload.refunded === true)
    && (
      payload.recordNo != null
      || payload.balanceAfter != null
      || payload.computeDelta != null
      || payload.computeAmount != null
    )
  ) {
    return true;
  }

  return Object.values(payload).some((value) => payloadHasComputeMutation(value, depth + 1));
}

function shouldRefreshComputeBalance(response: AxiosResponse<ApiResponse>, payload: unknown) {
  const method = String(response.config.method || '').toLowerCase();
  if (method === 'get') {
    return false;
  }

  const url = String(response.config.url || '');
  if (COMPUTE_BALANCE_REFRESH_URL_PARTS.some((part) => url.includes(part))) {
    return true;
  }

  return payloadHasComputeMutation(payload);
}

function isDemoBlockedRequest(config: InternalAxiosRequestConfig) {
  const method = String(config.method || 'get').toLowerCase();
  if (method === 'get') {
    return false;
  }
  if (method === 'delete') {
    return true;
  }
  const url = String(config.url || '');
  return DEMO_BLOCKED_URL_PARTS.some((part) => url.includes(part));
}

function parseRequestData(data: unknown): Record<string, unknown> {
  if (!data) {
    return {};
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }
  return typeof data === 'object' && !Array.isArray(data) ? data as Record<string, unknown> : {};
}

function resolveDemoComputeScenarioCode(config: InternalAxiosRequestConfig) {
  const method = String(config.method || 'get').toLowerCase();
  if (method === 'get') {
    return '';
  }
  const url = String(config.url || '');
  const matched = DEMO_COMPUTE_POLICY_REQUESTS.find((item) => item.parts.some((part) => url.includes(part)));
  if (!matched) {
    return '';
  }

  if (matched.scenarioCode !== 'CREATIVE_WRITER_VIDEO_GENERATION') {
    return matched.scenarioCode;
  }

  const data = parseRequestData(config.data);
  const hasReference = (
    Array.isArray(data.reference_image_urls) && data.reference_image_urls.length > 0
  ) || (
    Array.isArray(data.reference_video_urls) && data.reference_video_urls.length > 0
  );
  return hasReference
    ? 'CREATIVE_WRITER_VIDEO_GENERATION_WITH_REFERENCE'
    : 'CREATIVE_WRITER_VIDEO_GENERATION_WITHOUT_REFERENCE';
}

function isDemoComputeBlockedRequest(
  config: InternalAxiosRequestConfig,
  demoComputePolicies?: Record<string, string>
) {
  const scenarioCode = resolveDemoComputeScenarioCode(config);
  if (!scenarioCode) {
    return false;
  }
  return String(demoComputePolicies?.[scenarioCode] || '').toUpperCase() === 'BLOCKED';
}

function extractApiErrorPayload(response?: AxiosError<ApiResponse>['response']) {
  const data = response?.data;
  if (!data || typeof data !== 'object') {
    return null;
  }

  return {
    code: typeof data.code === 'number' ? data.code : undefined,
    message: typeof data.message === 'string' ? data.message.trim() : '',
  };
}

async function clearNativeAuthSession() {
  if (isClearingApiAuthSession || !window.electronAPI) {
    return;
  }

  isClearingApiAuthSession = true;
  try {
    await window.electronAPI.invoke(IPC.CLEAR_API_AUTH_SESSION);
  } catch (error) {
    console.error('Failed to clear native auth session.', error);
  } finally {
    isClearingApiAuthSession = false;
  }
}

function handleExpiredSession(message?: string, meta: RequestErrorMeta = {}) {
  console.error('Session expired, redirecting to login...');
  void clearNativeAuthSession();
  useAppStore.getState().clearAuthSession();
  window.location.hash = '#/login';
  return Promise.reject(createRequestError(message || '登录已过期，请重新登录', meta));
}

// 2. 请求拦截器
request.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Cookie Session 模式：浏览器自动携带 sid Cookie，无需手动设置 Authorization
    // 客户端版本头统一从 package.json 读取，避免每个接口单独拼接版本参数。
    if (CLIENT_VERSION) {
      config.headers.set(CLIENT_VERSION_HEADER_NAME, CLIENT_VERSION);
    }

    if (CLIENT_BUILD_VERSION) {
      config.headers.set(CLIENT_BUILD_VERSION_HEADER_NAME, CLIENT_BUILD_VERSION);
    }

    const { dataSpaceState } = useAppStore.getState();
    if (dataSpaceState.dataSpace === 'DEMO') {
      if (isDemoBlockedRequest(config)) {
        return Promise.reject(createRequestError('演示数据模式下不支持该操作', {
          code: 3003,
        }));
      }
      if (isDemoComputeBlockedRequest(config, dataSpaceState.demoWorkspace?.demoComputePolicies)) {
        return Promise.reject(createRequestError('演示数据模式下不支持该操作', {
          code: 3003,
        }));
      }
      config.headers.set('x-data-space', 'DEMO');
      if (dataSpaceState.demoWorkspace?.datasetId) {
        config.headers.set('x-demo-dataset-id', dataSpaceState.demoWorkspace.datasetId);
      }
      if (dataSpaceState.demoWorkspace?.scenario) {
        config.headers.set('x-demo-scenario', String(dataSpaceState.demoWorkspace.scenario));
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 3. 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { code, data, message } = response.data;

    // 根据业务定制成功状态码（code === 0 表示成功）
    if (code === 200 || code === 0) {
      if (shouldRefreshComputeBalance(response, data)) {
        queueMicrotask(() => {
          dispatchAccountPaymentDataUpdated({ reason: 'compute_mutation' });
        });
      }
      return data; // 直接提取真正的负载数据，让组件少写 .data.data
    }

    // 认证相关业务码统一跳回登录页，3005 表示单点登录限制下被顶号。
    if (isAuthRedirectCode(code)) {
      return handleExpiredSession(message || '当前账号已在其他设备登录，请重新登录', {
        code,
        status: response.status,
      });
    }

    // 登录/验证码接口的受邀限制单独透出，避免落成泛化错误文案。
    if (code === AUTH_RESPONSE_CODES.INVITE_ONLY) {
      return Promise.reject(createRequestError(message || '当前仅开放受邀用户获取验证码', {
        code,
        status: response.status,
      }));
    }

    handlePaymentBusinessError(code);

    // 处理其他业务错误
    console.error('Business Error:', message);
    return Promise.reject(createRequestError(message || 'Error', {
      code,
      status: response.status,
    }));
  },
  (error: AxiosError<ApiResponse>) => {
    const { response } = error;
    const apiErrorPayload = extractApiErrorPayload(response);

    if (response) {
      if (
        isAuthRedirectCode(apiErrorPayload?.code)
      ) {
        return handleExpiredSession(apiErrorPayload?.message || '当前账号已在其他设备登录，请重新登录', {
          code: apiErrorPayload?.code,
          status: response.status,
        });
      }

      if (apiErrorPayload?.code === AUTH_RESPONSE_CODES.INVITE_ONLY) {
        return Promise.reject(createRequestError(apiErrorPayload.message || '当前仅开放受邀用户获取验证码', {
          code: apiErrorPayload.code,
          status: response.status,
        }));
      }

      handlePaymentBusinessError(apiErrorPayload?.code);

      switch (response.status) {
        case 401:
          return handleExpiredSession(apiErrorPayload?.message || '登录已过期，请重新登录', {
            code: apiErrorPayload?.code,
            status: response.status,
          });
        case 403:
          console.error('Forbidden access.', apiErrorPayload?.message || '');
          break;
        case 404:
          console.error('Resource not found.');
          break;
        case 500:
          console.error('Internal Server Error.');
          break;
        default:
          console.error(`Error Payload: ${response.status}`);
      }
    } else {
      console.error('Network Error. Please check your connection.');
    }

    const fallbackMessage = apiErrorPayload?.message
      || (typeof error.message === 'string' && error.message.trim())
      || '请求失败，请稍后重试';

    return Promise.reject(createRequestError(fallbackMessage, {
      code: apiErrorPayload?.code,
      status: response?.status,
    }));
  }
);

// 提取一个更友好的泛型请求方法
/* eslint-disable @typescript-eslint/no-explicit-any */
export const http = {
  get<T = any>(url: string, config?: any): Promise<T> {
    return request.get(url, config);
  },
  post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return request.post(url, data, config);
  },
  put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return request.put(url, data, config);
  },
  delete<T = any>(url: string, config?: any): Promise<T> {
    return request.delete(url, config);
  },
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export default request;
